import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { db } from "../firebase";
import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  orderBy,
  startAfter,
  limit,
  serverTimestamp,
  updateDoc,
  setDoc,
} from "firebase/firestore";
import { useAuth } from "../contexts/AuthContext";
import "./ChatView.css";

const ChatView = () => {
  const { chatId } = useParams();
  const { currentUser } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [chatInfo, setChatInfo] = useState(null);
  const [typing, setTyping] = useState(false);  // Fixing the typing state
  const [lastVisible, setLastVisible] = useState(null);
  const bottomRef = useRef(null);
  const topRef = useRef(null);

  useEffect(() => {
    if (!chatId) return;

    // Fetch Chat Info (Trade Title, Price, Item Image, Online Status)
    const fetchChatInfo = async () => {
      const chatDoc = await getDoc(doc(db, "Chats", chatId));
      if (chatDoc.exists()) {
        setChatInfo(chatDoc.data());
      }
    };
    fetchChatInfo();

    // Fetch latest 50 messages
    const q = query(
      collection(db, "Chats", chatId, "messages"),
      orderBy("timestamp", "desc"),
      limit(50)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const msgs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setMessages(msgs.reverse());
      setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
    });

    // Setup Typing Status
    const typingDocRef = doc(db, "Chats", chatId, "typingStatus", currentUser.uid);
    const unsubscribeTyping = onSnapshot(typingDocRef, (doc) => {
      if (doc.exists()) {
        const typingStatus = doc.data().typing;
        setTyping(typingStatus);  // Update typing state based on Firestore data
      }
    });

    setDoc(typingDocRef, { typing: false }, { merge: true });

    return () => {
      unsubscribe();
      unsubscribeTyping();  // Unsubscribe from typing status listener when component unmounts
    };
  }, [chatId, currentUser.uid]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleTyping = async (e) => {
    setNewMsg(e.target.value);

    const typingDocRef = doc(db, "Chats", chatId, "typingStatus", currentUser.uid);
    await setDoc(typingDocRef, { typing: e.target.value.length > 0 }, { merge: true });
  };

  const sendMessage = async () => {
    if (!newMsg.trim()) return;

    await addDoc(collection(db, "Chats", chatId, "messages"), {
      text: newMsg.trim(),
      sender: currentUser.uid,
      timestamp: serverTimestamp(),
      status: "sent", // could be seen later
    });

    await updateDoc(doc(db, "Chats", chatId), {
      lastUpdated: serverTimestamp(),
    });

    const typingDocRef = doc(db, "Chats", chatId, "typingStatus", currentUser.uid);
    await setDoc(typingDocRef, { typing: false }, { merge: true });

    setNewMsg("");
  };

  const loadMoreMessages = async () => {
    if (!lastVisible) return;

    const currentScrollPosition = topRef.current?.getBoundingClientRect().top;

    const q = query(
      collection(db, "Chats", chatId, "messages"),
      orderBy("timestamp", "desc"),
      startAfter(lastVisible),
      limit(50)
    );

    const snapshot = await getDocs(q);
    const moreMsgs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    if (moreMsgs.length > 0) {
      setMessages(prev => [...moreMsgs.reverse(), ...prev]);
      setLastVisible(snapshot.docs[snapshot.docs.length - 1]);

      // Maintain scroll position (to avoid jump effect)
      setTimeout(() => {
        if (topRef.current) {
          const newTopPosition = topRef.current.getBoundingClientRect().top;
          const delta = newTopPosition - currentScrollPosition;
          window.scrollBy(0, delta);
        }
      }, 50);
    }
  };

  const handleScroll = (e) => {
    const { scrollTop } = e.target;
    if (scrollTop === 0 && lastVisible) {
      loadMoreMessages();
    }
  };

  return (
    <div className="chat-container">
      {/* HEADER */}
      <div className="chat-header">
        {chatInfo && (
          <div className="chat-item-info">
            <div className="chat-avatar-wrapper">
              <img src={chatInfo.itemImage || "/placeholder.png"} alt={chatInfo.tradeTitle} />
              {chatInfo.onlineStatus?.otherUser && (
                <span className="online-dot"></span>
              )}
            </div>
            <div>
              <h4>{chatInfo.tradeTitle || "Trade Item"}</h4>
              <p>R{chatInfo.estimatedValue ?? "0"}</p>
            </div>
          </div>
        )}
      </div>

      {/* MESSAGES */}
      <div className="chat-messages" onScroll={handleScroll}>
        <div ref={topRef}></div>

        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`chat-bubble ${msg.sender === currentUser.uid ? "sent" : "received"}`}
          >
            <p className="chat-text">{msg.text}</p>
            <small className="chat-time">
              {msg.timestamp?.toDate
                ? new Date(msg.timestamp.toDate()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                : ""}
              {msg.sender === currentUser.uid && (
                msg.status === "seen" ? " 👁️" : " ✔️"
              )}
            </small>
          </div>
        ))}

        {/* Typing Indicator */}
        {typing && (
          <div className="chat-typing-indicator">
            ✍️ Other user is typing...
          </div>
        )}
        <div ref={bottomRef}></div>
      </div>

      {/* INPUT */}
      <div className="chat-input-bar">
        <input
          type="text"
          placeholder="Type a message..."
          value={newMsg}
          onChange={handleTyping}
          onKeyDown={(e) => { if (e.key === 'Enter') sendMessage(); }}
        />
        <button onClick={sendMessage}>Send</button>
      </div>
    </div>
  );
};

export default ChatView;
